import {
  collection,
  getDocs,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import { stripUndefined } from './stock/stockLedgerService'
import type {
  Client,
  UpsertClientInput,
  ArchiveClientInput,
  RestoreClientInput,
  EraseClientInput,
} from '../types/client'

export const KNOWN_CLIENT_OUTREACH_ERROR_CODES = [
  'CLIENT_OUTREACH_DISABLED',
  'MODULE_DISABLED',
  'PAYWALL_REQUIRED',
  'SUBSCRIPTION_RESTRICTED',
  'CLIENT_EMAIL_EXISTS',
  'CLIENT_OUTREACH_DAILY_CAP',
  'PERMISSION_DENIED',
  'NOT_FOUND',
  'INVALID_ARGUMENT',
  'SENDER_EMAIL_UNVERIFIED',
  'APP_BASE_URL_MISSING',
] as const

export type KnownClientOutreachErrorCode = (typeof KNOWN_CLIENT_OUTREACH_ERROR_CODES)[number]

export class ClientOutreachError extends Error {
  readonly code: string
  readonly functionsCode?: string
  readonly details?: unknown

  constructor(
    code: string,
    message: string,
    options?: { functionsCode?: string; details?: unknown; cause?: unknown },
  ) {
    super(message)
    this.name = 'ClientOutreachError'
    this.code = code
    this.functionsCode = options?.functionsCode
    this.details = options?.details
    this.cause = options?.cause
    Object.setPrototypeOf(this, ClientOutreachError.prototype)
  }
}

const KNOWN_CODES_SET = new Set<string>(KNOWN_CLIENT_OUTREACH_ERROR_CODES)

export function mapToClientOutreachError(err: unknown): ClientOutreachError {
  if (err instanceof ClientOutreachError) return err

  const errObj =
    typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : undefined
  const maybeCode =
    errObj && 'code' in errObj && typeof errObj.code === 'string' ? errObj.code : undefined
  const maybeMsg =
    errObj && 'message' in errObj && typeof errObj.message === 'string'
      ? errObj.message
      : 'Client outreach operation failed.'
  const details = errObj && 'details' in errObj ? errObj.details : undefined

  let code: string = maybeCode ?? 'unknown'
  if (KNOWN_CODES_SET.has(maybeMsg)) {
    code = maybeMsg
  } else if (typeof details === 'string' && KNOWN_CODES_SET.has(details)) {
    code = details
  } else if (typeof details === 'object' && details !== null) {
    const detailsObj = details as Record<string, unknown>
    if ('code' in detailsObj && typeof detailsObj.code === 'string') {
      code = detailsObj.code
    }
  } else if (maybeCode && maybeCode !== 'unknown') {
    code = maybeCode
  }

  return new ClientOutreachError(code, maybeMsg, {
    functionsCode: maybeCode,
    details,
    cause: err,
  })
}

function normalizeClientDoc(id: string, data: Record<string, unknown>): Client {
  return {
    id,
    name: String(data.name ?? ''),
    email: String(data.email ?? ''),
    emailNormalized: String(data.emailNormalized ?? ''),
    company: typeof data.company === 'string' ? data.company : undefined,
    phone: typeof data.phone === 'string' ? data.phone : undefined,
    notes: typeof data.notes === 'string' ? data.notes : undefined,
    archived: data.archived === true,
    archivedAt: data.archivedAt,
    archivedBy: typeof data.archivedBy === 'string' ? data.archivedBy : undefined,
    blocked: data.blocked as Client['blocked'],
    erased: data.erased === true,
    erasedAt: data.erasedAt,
    erasedBy: typeof data.erasedBy === 'string' ? data.erasedBy : undefined,
    currency: typeof data.currency === 'string' ? data.currency : undefined,
    billingMode: (data.billingMode as import('../types/timesheets').ClientBillingMode) || 'HOURLY',
    defaultBillRate: typeof data.defaultBillRate === 'number' ? data.defaultBillRate : undefined,
    createdAt: data.createdAt,
    createdBy: String(data.createdBy ?? ''),
    updatedAt: data.updatedAt,
    updatedBy: String(data.updatedBy ?? ''),
  }
}

function filterAndSortClients(clients: Client[], includeArchived = false): Client[] {
  return clients
    .filter((c) => !c.erased && (includeArchived || !c.archived))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

/**
 * listClients — retrieves the list of clients for a workspace.
 */
export async function listClients(
  workspaceId: string,
  options?: { includeArchived?: boolean },
): Promise<Client[]> {
  if (!workspaceId) return []
  const clientsCol = collection(firestoreDb, 'workspaces', workspaceId, 'clients')
  const snap = await getDocs(clientsCol)
  const rawClients = snap.docs.map((d) => normalizeClientDoc(d.id, d.data() as Record<string, unknown>))
  return filterAndSortClients(rawClients, options?.includeArchived)
}

/**
 * subscribeToClients — subscribes to real-time updates for workspace clients.
 */
export function subscribeToClients(
  workspaceId: string,
  callback: (clients: Client[]) => void,
  options?: { includeArchived?: boolean },
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const clientsCol = collection(firestoreDb, 'workspaces', workspaceId, 'clients')
  return onSnapshot(
    clientsCol,
    (snap) => {
      const rawClients = snap.docs.map((d) => normalizeClientDoc(d.id, d.data() as Record<string, unknown>))
      callback(filterAndSortClients(rawClients, options?.includeArchived))
    },
    (err) => {
      onError?.(mapToClientOutreachError(err))
    },
  )
}

/**
 * upsertClient — creates or updates a client via Cloud Function callable.
 */
export async function upsertClient(input: UpsertClientInput): Promise<{ clientId: string }> {
  try {
    const payload = stripUndefined(input)
    return await callable<UpsertClientInput, { clientId: string }>('upsertClient', payload)
  } catch (err) {
    throw mapToClientOutreachError(err)
  }
}

/**
 * archiveClient — marks a client as archived via Cloud Function callable.
 */
export async function archiveClient(input: ArchiveClientInput): Promise<{ clientId: string }> {
  try {
    const payload = stripUndefined(input)
    return await callable<ArchiveClientInput, { clientId: string }>('archiveClient', payload)
  } catch (err) {
    throw mapToClientOutreachError(err)
  }
}

/**
 * restoreClient — restores an archived client via Cloud Function callable.
 */
export async function restoreClient(input: RestoreClientInput): Promise<{ clientId: string }> {
  try {
    const payload = stripUndefined(input)
    return await callable<RestoreClientInput, { clientId: string }>('restoreClient', payload)
  } catch (err) {
    throw mapToClientOutreachError(err)
  }
}

/**
 * eraseClient — erases client data via Cloud Function callable.
 */
export async function eraseClient(
  input: EraseClientInput,
): Promise<{ clientId: string; erased: boolean }> {
  try {
    const payload = stripUndefined(input)
    return await callable<EraseClientInput, { clientId: string; erased: boolean }>(
      'eraseClient',
      payload,
    )
  } catch (err) {
    throw mapToClientOutreachError(err)
  }
}
